import { useContext, useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { Box, Typography, useTheme, Button } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { GlobalContext } from '../contexts/global-context';
import { ModelCarousel } from './model-carousel';
import Skeleton from '@mui/material/Skeleton';
import { useConfig } from '../contexts/config-context';
import useApi from '../services/api';
import { WebsiteStructuredData, OrganizationStructuredData } from './structured-data';
import { FeaturedPlanCard } from './featured-plan-card';
import BuildIcon from '@mui/icons-material/Build';
import HandymanIcon from '@mui/icons-material/Handyman';
import ConstructionIcon from '@mui/icons-material/Construction';
import CarpenterIcon from '@mui/icons-material/Carpenter';
import GridOnIcon from '@mui/icons-material/GridOn';
import StraightenIcon from '@mui/icons-material/Straighten';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import StepOne from '../assets/images/step-1-infographic';
import StepTwo from '../assets/images/step-2-infographic';
import StepThree from '../assets/images/step-3-infographic';
import BlueprintGrid from './blueprint-grid';

export function Home() {
    const { isMobile } = useContext(GlobalContext);
    const navigate = useNavigate();
    const { getModels, getModel } = useApi();
    const theme = useTheme();
    const { error: configError, config } = useConfig();
    
    const [featuredModels, setFeaturedModels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [carouselImagesLoaded, setCarouselImagesLoaded] = useState(false);
    const [featuredModel, setFeaturedModel] = useState(null);
    
    useEffect(() => {
        const loadFeaturedModels = async () => {
            try {
                // Load first page with 6 models for the carousel
                const response = await getModels(1, 6);
                setFeaturedModels(response.results || []);
            } catch (error) {
                console.error('Failed to load featured models:', error);
            } finally {
                setLoading(false);
            }
        };
        
        loadFeaturedModels();
    }, [getModels]);
    
    // Handler for when carousel images are loaded
    const handleCarouselImagesLoaded = () => {
        setCarouselImagesLoaded(true);
    };
    
    // If no models, consider carousel "loaded"
    useEffect(() => {
        if (!loading && featuredModels.length === 0) {
            setCarouselImagesLoaded(true);
        }
    }, [loading, featuredModels]);

    // Fetch the featured model from config
    useEffect(() => {
        if (!config.featured_model) return;
        getModel(config.featured_model)
            .then(response => setFeaturedModel(response))
            .catch(err => console.error('Failed to load featured model:', err));
    }, [config.featured_model, getModel]);
    
    return (
        <>
            <Helmet>
                <title>PlanCrafted - Custom Woodworking Plans with 3D Previews</title>
                <meta name="description" content="Generate customizable woodworking plans with 3D previews. Select a design, customize dimensions and features, then download workshop-ready plans with cut lists and assembly diagrams." />
                <link rel="canonical" href="https://plancrafted.com/" />
                <meta property="og:type" content="website" />
                <meta property="og:title" content="PlanCrafted - Custom Woodworking Plans with 3D Previews" />
                <meta property="og:description" content="Generate customizable woodworking plans with 3D previews. Select a design, customize dimensions and features, then download workshop-ready plans." />
                <meta property="og:url" content="https://plancrafted.com/" />
                <meta property="og:site_name" content="PlanCrafted" />
                <meta property="og:image" content="https://static.plancrafted.com/img/fb-og.jpg" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="PlanCrafted - Custom Woodworking Plans with 3D Previews" />
                <meta name="twitter:description" content="Generate customizable woodworking plans with 3D previews. Select a design, customize dimensions and features, then download workshop-ready plans." />
                <meta name="twitter:image" content="https://static.plancrafted.com/img/fb-og.jpg" />
            </Helmet>
            <WebsiteStructuredData />
            <OrganizationStructuredData />

            {/* How It Works */}
            <Box sx={{
                width: '100%',
                mt: 0,
                mb: 0,
                pt: { xs: 3, md: 8 },
                pb: { xs: 6, md: 8 },
            }}>
                {/* Header */}
                <Box sx={{
                    textAlign: 'center',
                    maxWidth: '620px',
                    mx: 'auto',
                    px: 3,
                    mb: { xs: 5, md: 7 },
                }}>
                    <Typography sx={{
                        fontSize: '12px',
                        letterSpacing: '0.24em',
                        textTransform: 'uppercase',
                        color: 'woodDark',
                        fontWeight: 600,
                    }}>
                        How it works
                    </Typography>
                    <Typography variant="h2" sx={{
                        fontWeight: 400,
                        fontSize: { xs: '2rem', md: '3rem' },
                        mt: 1.5,
                        color: 'text.primary',
                    }}>
                        Three steps.
                    </Typography>
                    <Typography sx={{
                        fontSize: '17px',
                        color: 'ink2',
                        lineHeight: 1.6,
                        mt: 1.5,
                    }}>
                        Pick a design. Dial it in. Cut with confidence.
                    </Typography>
                </Box>

                {/* Steps */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                    gap: { xs: 3, md: '30px' },
                    maxWidth: '1100px',
                    mx: 'auto',
                    px: 3,
                }}>
                    {[
                        {
                            num: '1',
                            Illustration: StepOne,
                            iconSize: 62,
                            title: 'Select a design',
                            desc: 'Browse projects engineered to adapt. Pick one and make it the starting point.',
                        },
                        {
                            num: '2',
                            Illustration: StepTwo,
                            iconSize: 70,
                            title: 'Customize it',
                            desc: 'Adjust dimensions, materials, and features. Watch it come together in 3D.',
                        },
                        {
                            num: '3',
                            Illustration: StepThree,
                            iconSize: 62,
                            title: 'Download & build',
                            desc: 'Complete plans with cut lists, measurements, and assembly drawings.',
                        },
                    ].map((step) => (
                        <Box key={step.num} sx={{
                            textAlign: isMobile ? 'left' : 'center',
                            display: isMobile ? 'flex' : 'block',
                            alignItems: isMobile ? 'flex-start' : undefined,
                            gap: isMobile ? 2 : undefined,
                        }}>
                            {/* Disc with number */}
                            <Box sx={{
                                width: isMobile ? 72 : 120,
                                height: isMobile ? 72 : 120,
                                minWidth: isMobile ? 72 : 120,
                                borderRadius: '50%',
                                backgroundColor: 'paper2',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: isMobile ? undefined : 'auto',
                                position: 'relative',
                            }}>
                                <step.Illustration style={{
                                    width: isMobile ? Math.round(step.iconSize * 0.6) : step.iconSize,
                                    height: isMobile ? Math.round(step.iconSize * 0.6) : step.iconSize,
                                    filter: 'brightness(0)',
                                }} />
                                <Typography sx={{
                                    position: 'absolute',
                                    top: isMobile ? -4 : -6,
                                    right: isMobile ? 2 : 6,
                                    fontSize: isMobile ? '30px' : '42px',
                                    fontWeight: 400,
                                    color: 'primary.main',
                                    lineHeight: 1,
                                }}>
                                    {step.num}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="h3" sx={{
                                    fontSize: isMobile ? '18px' : '22px',
                                    fontWeight: 600,
                                    mt: isMobile ? 0.5 : 3,
                                    mb: 0.5,
                                    color: 'text.primary',
                                }}>
                                    {step.title}
                                </Typography>
                                <Typography sx={{
                                    fontSize: isMobile ? '14px' : '15px',
                                    color: 'ink2',
                                    lineHeight: 1.6,
                                    maxWidth: isMobile ? undefined : '300px',
                                    mx: isMobile ? undefined : 'auto',
                                }}>
                                    {step.desc}
                                </Typography>
                                {step.num === '1' && !isMobile && (
                                    <Button
                                        variant="contained"
                                        onClick={() => navigate('/designs')}
                                        sx={{
                                            mt: isMobile ? 1.5 : 2.5,
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            fontSize: '15px',
                                            px: 3,
                                            py: 1,
                                        }}
                                    >
                                        Start Designing
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Featured Design Card */}
            {featuredModel && (
                <Box sx={{ width: '100%', maxWidth: '1100px', mx: 'auto', px: 3, mb: { xs: 4, md: 6 } }}>
                    <FeaturedPlanCard model={featuredModel} />
                </Box>
            )}

            {/* Optimizer Section */}
            <BlueprintGrid sx={{ width: '100%', py: { xs: 6, md: 8 } }}>
                <Box sx={{
                    maxWidth: '1100px',
                    mx: 'auto',
                    px: 3,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' },
                    gap: { xs: 4, md: '46px' },
                    alignItems: 'center',
                }}>
                    {/* Sheet grid visualization */}
                    <Box sx={{
                        aspectRatio: '16/10',
                        border: '1px solid rgba(196,164,127,.16)',
                        borderRadius: '12px',
                        backgroundColor: '#0a1120',
                        p: '12px',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(8, 1fr)',
                        gridTemplateRows: 'repeat(6, 1fr)',
                        gap: '6px',
                    }}>
                        <Box sx={{ gridColumn: '1/4', gridRow: '1/4', backgroundColor: '#5B9BD5', borderRadius: '4px' }} />
                        <Box sx={{ gridColumn: '4/7', gridRow: '1/3', backgroundColor: '#E87DA0', borderRadius: '4px' }} />
                        <Box sx={{ gridColumn: '7/9', gridRow: '1/5', backgroundColor: '#7FBF7F', borderRadius: '4px' }} />
                        <Box sx={{ gridColumn: '1/3', gridRow: '4/7', backgroundColor: '#E8D44D', borderRadius: '4px' }} />
                        <Box sx={{ gridColumn: '3/4', gridRow: '4/7', backgroundColor: '#E8A44D', borderRadius: '4px' }} />
                        <Box sx={{ gridColumn: '4/7', gridRow: '3/5', backgroundColor: '#5B9BD5', opacity: 0.7, borderRadius: '4px' }} />
                        <Box sx={{ gridColumn: '4/7', gridRow: '5/7', backgroundColor: '#E87DA0', opacity: 0.7, borderRadius: '4px' }} />
                        <Box sx={{ gridColumn: '7/9', gridRow: '5/7', backgroundColor: 'rgba(255,255,255,.06)', border: '1px dashed rgba(196,164,127,.16)', borderRadius: '4px' }} />
                    </Box>

                    {/* Text content */}
                    <Box>
                        <Typography sx={{
                            fontSize: '12px',
                            letterSpacing: '0.24em',
                            textTransform: 'uppercase',
                            color: '#C4A47F',
                            fontWeight: 600,
                        }}>
                            Optimizer
                        </Typography>
                        <Typography variant="h2" sx={{
                            fontWeight: 400,
                            fontSize: { xs: '1.5rem', md: '2.25rem' },
                            mt: 1.5,
                            color: '#EAF0F8',
                        }}>
                            Smarter layouts. Less wasted material.
                        </Typography>
                        <Typography sx={{
                            fontSize: '16px',
                            color: '#B9C6DA',
                            lineHeight: 1.6,
                            mt: 1.5,
                        }}>
                            Every plan nests parts across full sheets for maximum yield. Color-coded panels, sized to your stock, ready for the saw.
                        </Typography>

                        {/* 2×2 feature grid */}
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: '22px 40px',
                            mt: '30px',
                        }}>
                            {[
                                { Icon: GridOnIcon, title: 'Sheet-by-sheet layout', desc: 'Scaled to 4×8 or custom stock.' },
                                { Icon: StraightenIcon, title: 'Fully dimensioned', desc: 'Every view fully dimensioned.' },
                                { Icon: ViewInArIcon, title: 'Exploded assembly', desc: '3D views showing build order.' },
                                { Icon: SwapHorizIcon, title: 'Imperial or metric', desc: 'Inches or millimeters — your call.' },
                            ].map((feat) => (
                                <Box key={feat.title} sx={{ display: 'flex', gap: '14px' }}>
                                    <feat.Icon sx={{ color: '#C4A47F', fontSize: 24, mt: '2px', flexShrink: 0 }} />
                                    <Box>
                                        <Typography sx={{ fontWeight: 600, fontSize: '16px', mb: '4px', color: '#EAF0F8' }}>
                                            {feat.title}
                                        </Typography>
                                        <Typography sx={{ fontSize: '14px', color: '#9DACC4', lineHeight: 1.55 }}>
                                            {feat.desc}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </BlueprintGrid>

            {/* Featured Models Carousel */}
            <Box sx={{
                width: '100%',
                mt: 2,
                mb: 2
            }}>
                {/* Catalog Header */}
                <Box sx={{
                    maxWidth: '1536px',
                    mx: 'auto',
                    px: { xs: 2, md: 4 },
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    mb: { xs: 3, md: 4 },
                }}>
                    <Box>
                        <Typography sx={{
                            fontSize: '12px',
                            letterSpacing: '0.24em',
                            textTransform: 'uppercase',
                            color: 'woodDark',
                            fontWeight: 600,
                        }}>
                            The catalog
                        </Typography>
                        <Typography variant="h2" sx={{
                            fontWeight: 400,
                            fontSize: { xs: '1.75rem', md: '2.875rem' },
                            mt: 1,
                            color: 'text.primary',
                        }}>
                            Designs worth building.
                        </Typography>
                    </Box>
                    <Typography
                        component="a"
                        onClick={() => navigate('/designs')}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            color: 'woodDark',
                            fontWeight: 600,
                            fontSize: '15px',
                            cursor: 'pointer',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                            whiteSpace: 'nowrap',
                        }}
                    >
                        View all
                    </Typography>
                </Box>

                <Box sx={{
                    maxWidth: '1536px',
                    mx: 'auto',
                    px: { xs: 2, md: 4 },
                    minHeight: isMobile ? (280 + 74 + 16) : (400 + 74 + 16),
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    {/* Show Coming Soon if config failed (API unavailable) */}
                    {configError ? (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                            textAlign: 'center',
                            position: 'relative',
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}10 100%)`,
                            borderRadius: 2,
                            overflow: 'hidden'
                        }}>
                            {/* Floating Tool Icons Background */}
                            <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                opacity: 0.05,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-around',
                                flexWrap: 'wrap',
                                gap: { xs: 4, md: 8 },
                                p: 4
                            }}>
                                <HandymanIcon sx={{ fontSize: { xs: 80, md: 120 }, transform: 'rotate(-15deg)' }} />
                                <BuildIcon sx={{ fontSize: { xs: 70, md: 100 }, transform: 'rotate(20deg)' }} />
                                <ConstructionIcon sx={{ fontSize: { xs: 90, md: 140 }, transform: 'rotate(-10deg)' }} />
                                <CarpenterIcon sx={{ fontSize: { xs: 75, md: 110 }, transform: 'rotate(25deg)' }} />
                                <HandymanIcon sx={{ fontSize: { xs: 65, md: 95 }, transform: 'rotate(-25deg)' }} />
                            </Box>

                            {/* Main Content */}
                            <Box sx={{ zIndex: 2, position: 'relative' }}>
                                <Typography
                                    variant="h1"
                                    sx={{
                                        fontWeight: 900,
                                        fontSize: { xs: '3.5rem', md: '6rem', lg: '7rem' },
                                        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                        backgroundClip: 'text',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
                                        mb: 3,
                                        letterSpacing: '-0.02em'
                                    }}
                                >
                                    SITE MAINTENANCE
                                </Typography>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontSize: { xs: '1.2rem', md: '1.8rem' },
                                        fontWeight: 600,
                                        color: theme.palette.text.primary,
                                        mb: 2,
                                        textAlign: 'center'
                                    }}
                                >
                                    We'll Be Right Back
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: { xs: '0.95rem', md: '1.1rem' },
                                        color: theme.palette.text.secondary,
                                        lineHeight: 1.6,
                                        textAlign: 'center',
                                        maxWidth: { xs: 320, md: 600 },
                                        mx: 'auto'
                                    }}
                                >
                                    We're currently performing site maintenance to improve your experience. Please check back soon!
                                </Typography>
                            </Box>
                        </Box>
                    ) : loading || (!carouselImagesLoaded && featuredModels.length > 0) ? (
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '16px'
                        }}>
                            {[0, 1, 2].map((i) => {
                                const cardW = isMobile ? 280 : 400;
                                return (
                                    <Box
                                        key={i}
                                        sx={{
                                            width: cardW,
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            flexShrink: 0,
                                            display: {
                                                xs: i === 0 ? 'block' : 'none',
                                                sm: i < 2 ? 'block' : 'none',
                                                md: 'block'
                                            }
                                        }}
                                    >
                                        <Skeleton variant="rounded" animation="wave" height={37} sx={{ borderRadius: 0 }} />
                                        <Skeleton variant="rounded" animation="wave" height={cardW} sx={{ borderRadius: 0 }} />
                                        <Skeleton variant="rounded" animation="wave" height={37} sx={{ borderRadius: 0 }} />
                                    </Box>
                                );
                            })}
                        </Box>
                    ) : null}

                    {!configError && !loading && featuredModels.length > 0 && (
                        <Box sx={{
                            opacity: carouselImagesLoaded ? 1 : 0,
                            animation: carouselImagesLoaded ? 'fadeIn 0.5s ease-in-out forwards' : 'none',
                            '@keyframes fadeIn': {
                                '0%': { opacity: 0 },
                                '100%': { opacity: 1 }
                            },
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: carouselImagesLoaded ? 'relative' : 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                        }}>
                            <ModelCarousel
                                models={config.featured_model
                                    ? featuredModels.filter(m => m.name !== config.featured_model)
                                    : featuredModels}
                                cardWidth={isMobile ? 280 : 400}
                                gap={16}
                                showArrows={true}
                                visibleCards={{ xs: 1, sm: 2, md: 3, lg: 3, xl: 3 }}
                                enableWrap={true}
                                showPreview={false}
                                onModelClick={(model) => navigate(`/designs/${model.name}`)}
                                onAllImagesLoaded={handleCarouselImagesLoaded}
                                hideIndividualLoading={true}
                            />
                        </Box>
                    )}

                    {!configError && !loading && featuredModels.length === 0 && (
                        <Typography
                            textAlign="center"
                            color="text.secondary"
                            sx={{ py: 4 }}
                        >
                            No featured models available
                        </Typography>
                    )}
                </Box>
            </Box>
        </>
    );
}